import { useCallback, useEffect, useState } from 'react';
import { socialApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { SocialAccountResponse, SocialReview, SocialReviewsSummary } from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function SocialReviewsPage({ embedded }: { embedded?: boolean } = {}) {
  const pagination = usePagination();
  const [reviews, setReviews] = useState<SocialReview[]>([]);
  const [accounts, setAccounts] = useState<SocialAccountResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyDraft, setReplyDraft] = useState<Record<number, string>>({});
  const [simulateAccountId, setSimulateAccountId] = useState<number | ''>('');
  const [simulateText, setSimulateText] = useState('Excellent service and fast response!');
  const [simulateRating, setSimulateRating] = useState('5');
  const [summary, setSummary] = useState<SocialReviewsSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [page, accs, sum] = await Promise.all([
        socialApi.listReviews({ page: pagination.page, size: pagination.size }),
        socialApi.listAccounts(),
        socialApi.reviewsSummary().catch(() => null),
      ]);
      setSummary(sum);
      setReviews(page.content ?? []);
      pagination.applyPageResponse(page);
      setAccounts(accs.filter((a) => a.connected));
    } catch (err) {
      showApiError(err, 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const reply = async (id: number) => {
    const text = replyDraft[id]?.trim();
    if (!text) return;
    try {
      await socialApi.replyReview(id, text);
      notify.success('Reply sent');
      load();
    } catch (err) {
      showApiError(err, 'Failed to reply');
    }
  };

  const simulateReview = async () => {
    if (!simulateAccountId) {
      notify.error('Select a connected channel');
      return;
    }
    try {
      await socialApi.simulateReview(Number(simulateAccountId), simulateText, Number(simulateRating));
      notify.success('Review ingested (simulated webhook)');
      load();
    } catch (err) {
      showApiError(err, 'Simulation failed');
    }
  };

  return (
    <div>
      {!embedded && (
      <PageHeader
        title="Reviews Center"
        description="Google and Facebook reviews — reply, assign, track sentiment, and convert to CRM leads"
        breadcrumbs={[{ label: 'Social', to: '/social' }, { label: 'Reviews' }]}
      />
      )}

      {!embedded && summary && (
        <KpiGrid columns={4} className="mb-4">
          <StatCard title="Total reviews" value={summary.totalReviews} />
          <StatCard title="Open" value={summary.openReviews} />
          <StatCard title="Replied" value={summary.repliedReviews} />
          <StatCard title="Avg rating" value={summary.averageRating.toFixed(1)} />
        </KpiGrid>
      )}

      <div className="card mb-4 grid gap-3 p-4 sm:grid-cols-4">
        <Select
          label="Simulate review (demo)"
          value={simulateAccountId === '' ? '' : String(simulateAccountId)}
          onChange={(v) => setSimulateAccountId(v ? Number(v) : '')}
          options={accounts.map((a) => ({ value: String(a.id), label: `${a.platform} — ${a.accountName}` }))}
        />
        <Input label="Review text" value={simulateText} onChange={(e) => setSimulateText(e.target.value)} />
        <Select
          label="Rating"
          value={simulateRating}
          onChange={setSimulateRating}
          options={['1', '2', '3', '4', '5'].map((n) => ({ value: n, label: `${n}★` }))}
        />
        <div className="flex items-end">
          <Button onClick={simulateReview}>Simulate webhook</Button>
        </div>
      </div>

      <div className="space-y-4">
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : reviews.length === 0 ? (
          <div className="card p-6 text-sm text-slate-500">
            No reviews yet. Connect a channel and use &quot;Simulate webhook&quot; above, or configure Google Business / Facebook for live sync.
          </div>
        ) : (
          reviews.map((r) => (
            <div key={r.id} className="card p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-medium">{r.platform}</span>
                <span className="text-amber-600">{r.rating != null ? `${r.rating}★` : '—'}</span>
              </div>
              <p className="mb-3 text-sm text-slate-700 dark:text-slate-300">{r.reviewText}</p>
              {r.replyText ? (
                <p className="mb-2 text-sm text-green-700 dark:text-green-400">Replied: {r.replyText}</p>
              ) : (
                <div className="flex items-end gap-2">
                  <Input
                    className="flex-1"
                    placeholder="Write a reply…"
                    value={replyDraft[r.id] ?? ''}
                    onChange={(e) => setReplyDraft({ ...replyDraft, [r.id]: e.target.value })}
                  />
                  <Button onClick={() => reply(r.id)}>Reply</Button>
                </div>
              )}
            </div>
          ))
        )}
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={reviews.length > 0}
        />
      </div>
    </div>
  );
}

